<template>
  <div class="bg-gray-50 rounded px-2 py-1 text-xs">
    <!-- Header -->
    <div class="flex items-center justify-between mb-1">
      <div class="flex items-center gap-2">
        <span class="font-semibold text-gray-700">{{ isCoderResponse ? 'Coder Response' : 'QA Review' }}</span>
        <span :class="getConcurrenceColorClass(concurrenceValue)"
              class="px-1.5 py-0.5 rounded text-xs border">
          {{ getResultText }}
        </span>
      </div>

      <!-- Delete Button -->
      <button
        v-if="allowDelete"
        @click.stop="confirmDelete"
        class="btn-danger-sm"
      >
        Delete
      </button>
    </div>

    <!-- Parent Code Reference -->
    <div v-if="parentWorkItem" class="mb-1">
      <span class="text-gray-500">Code:</span>
      <span class="font-medium text-gray-900 ml-1">{{ parentWorkItem.metadata?.code }}</span>
    </div>

    <!-- QA Status (Encounter workflow) -->
    <div v-if="!isCoderResponse && workflowType === 'encounter' && metadata.qa_status" class="mb-1">
      <span class="text-gray-500">Status:</span>
      <span :class="getStatusColorClass(metadata.qa_status)" class="ml-1 px-1.5 py-0.5 rounded text-xs border">
        {{ metadata.qa_status }}
      </span>
    </div>

    <!-- Disagreement Reason (Suspect workflow) -->
    <div v-if="!isCoderResponse && workflowType === 'suspect' && !metadata.qa_concurrence && metadata.qa_disagree_reason" class="mb-1">
      <span class="text-gray-500">Reason:</span>
      <span class="text-red-700 font-medium ml-1">{{ metadata.qa_disagree_reason }}</span>
    </div>

    <!-- Notes / Explanation -->
    <div v-if="explanationText" class="text-gray-600 mb-1">
      <span class="text-gray-500">{{ explanationLabel }}</span>
      {{ explanationText }}
    </div>

    <!-- Reviewer Info -->
    <div v-if="metadata.appuser_name || metadata.timestamp" class="mt-1 text-gray-500 text-xs flex items-center gap-2">
      <span v-if="metadata.appuser_name">Reviewed by {{ metadata.appuser_name }}</span>
      <span v-if="metadata.appuser_name && metadata.timestamp">•</span>
      <span v-if="metadata.timestamp">{{ formatTimestamp(metadata.timestamp) }}</span>
    </div>

    <!-- View Details Indicator -->
    <div v-if="compact && hasMoreDetails" class="mt-1 text-xs text-primary-600 flex items-center gap-1">
      <FontAwesomeIcon :icon="['fas', 'eye']" />
      <span>Click to view full details</span>
    </div>

    <!-- Delete Confirmation Modal -->
    <div v-if="showDeleteConfirm" class="fixed inset-0 z-50 flex items-center justify-center">
      <div class="fixed inset-0 bg-black/50" @click="cancelDelete"></div>
      <div class="relative bg-white rounded-lg p-4 max-w-sm mx-4">
        <h3 class="font-semibold text-gray-900 mb-2">Delete QA Review?</h3>
        <p class="text-sm text-gray-600 mb-4">
          This will permanently delete this QA review. This action cannot be undone.
        </p>
        <div class="flex justify-end gap-2">
          <button
            @click="cancelDelete"
            class="px-3 py-1 text-sm text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            @click="deleteQA"
            :disabled="deleting"
            class="btn-danger-sm"
          >
            <FontAwesomeIcon v-if="deleting" :icon="['fas', 'spinner']" class="animate-spin" />
            <span>{{ deleting ? 'Deleting...' : 'Delete' }}</span>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';

export default {
  name: 'QACodeSuggestionDisplay',
  components: {
    FontAwesomeIcon
  },
  props: {
    workItem: {
      type: Object,
      required: true
    },
    compact: {
      type: Boolean,
      default: true
    },
    parentWorkItem: {
      type: Object,
      default: null
    },
    currentUser: {
      type: Object,
      default: null
    }
  },
  emits: ['updated'],
  data() {
    return {
      showDeleteConfirm: false,
      deleting: false
    };
  },
  computed: {
    metadata() {
      return this.workItem.metadata;
    },
    workflowType() {
      // Get from metadata, fallback to 'encounter'
      return this.metadata?.workflow_type || 'encounter';
    },

    isCoderResponse() {
      const name = (this.workItem.work_type_name || this.workItem.work_type_details?.name || '').toLowerCase();
      return name.includes('coder_response') || name.includes('coder response');
    },

    concurrenceValue() {
      return this.isCoderResponse ? this.metadata?.coder_concurrence : this.metadata?.qa_concurrence;
    },

    explanationText() {
      if (this.isCoderResponse) {
        return this.metadata?.coder_notes;
      }
      return this.metadata?.qa_explanation;
    },

    explanationLabel() {
      if (this.isCoderResponse) {
        return this.metadata?.coder_concurrence ? 'Notes:' : 'Disagreement:';
      }
      return this.metadata?.qa_concurrence ? 'Notes:' : 'Explanation:';
    },

    allowDelete() {
      // Can delete if user created it
      return this.workItem.appuser_id === this.currentUser?.id;
    },

    getResultText() {
      if (this.isCoderResponse) {
        if (this.concurrenceValue === true) return 'Agreed';
        if (this.concurrenceValue === false) return 'Disagreed';
        return 'Unknown';
      }
      if (this.metadata.qa_concurrence === true) {
        if (this.workflowType === 'encounter') {
          return 'Agree';
        }
        return 'Confirmed';
      } else if (this.metadata.qa_concurrence === false) {
        if (this.workflowType === 'encounter') {
          return 'Disagree';
        }
        return 'Rejected';
      }
      return 'Unknown';
    },

    hasMoreDetails() {
      return (this.explanationText && this.explanationText.length > 100) ||
             (!this.isCoderResponse && this.metadata.qa_disagree_reason && this.metadata.qa_disagree_reason.length > 50);
    }
  },
  methods: {
    getConcurrenceColorClass(concurrence) {
      if (concurrence === true) {
        return 'bg-green-100 text-green-700 border-green-300';
      } else if (concurrence === false) {
        return 'bg-red-100 text-red-700 border-red-300';
      }
      return 'bg-gray-100 text-gray-700 border-gray-300';
    },

    getStatusColorClass(status) {
      if (!status) return 'bg-gray-100 text-gray-700 border-gray-300';

      if (status === 'Meat, Present') {
        return 'bg-green-100 text-green-700 border-green-300';
      } else if (status.includes('Missing')) {
        return 'bg-amber-100 text-amber-700 border-amber-300';
      }

      return 'bg-gray-100 text-gray-700 border-gray-300';
    },

    formatTimestamp(timestamp) {
      if (!timestamp) return '';

      const date = new Date(timestamp);
      const now = new Date();
      const diffMs = now - date;
      const diffMins = Math.floor(diffMs / 60000);
      const diffHours = Math.floor(diffMs / 3600000);
      const diffDays = Math.floor(diffMs / 86400000);

      if (diffMins < 1) return 'Just now';
      if (diffMins < 60) return `${diffMins}m ago`;
      if (diffHours < 24) return `${diffHours}h ago`;
      if (diffDays < 7) return `${diffDays}d ago`;

      return date.toLocaleDateString('en-US', {
        month: 'numeric',
        day: 'numeric',
        year: '2-digit'
      });
    },

    confirmDelete() {
      this.showDeleteConfirm = true;
    },

    cancelDelete() {
      this.showDeleteConfirm = false;
    },

    async deleteQA() {
      this.deleting = true;

      try {
        await axios.delete(`/api/work/${this.workItem.id}/`);
        this.showDeleteConfirm = false;
        this.$emit('work-deleted');
      } catch (error) {
        console.error('Error deleting QA:', error);
        alert('Failed to delete QA review');
      } finally {
        this.deleting = false;
      }
    }
  }
};
</script>

<style scoped>
.animate-spin {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}
</style>
